<template>
  <div class="sdk-request-logs-page w-100">
    <Wrapper>
      <!-- Chưa cấu hình gateway -->
      <b-alert v-if="!isConfigured" show variant="warning" class="mb-0">
        Chưa cấu hình <code>VUE_APP_SDK_GATEWAY</code> trong file môi trường.
      </b-alert>

      <!-- Cần đăng nhập -->
      <div v-else-if="needLogin" class="sdk-login-panel">
        <h5 class="mb-4">Đăng nhập Gateway quản trị SDK</h5>
        <form @submit.prevent="doLogin">
          <b-row>
            <b-col :cols="12" :md="5">
              <b-form-group label="Email">
                <b-form-input v-model="loginForm.email" placeholder="Nhập email admin" />
              </b-form-group>
            </b-col>
            <b-col :cols="12" :md="5">
              <b-form-group label="Mật khẩu">
                <b-form-input v-model="loginForm.password" type="password" placeholder="Nhập mật khẩu" />
              </b-form-group>
            </b-col>
            <b-col :cols="12" :md="2" class="d-flex align-items-end">
              <b-button type="submit" class="btn btn-success mb-3 w-100" :disabled="loginLoading">
                <b-spinner v-if="loginLoading" small class="mr-1" />
                Đăng nhập
              </b-button>
            </b-col>
          </b-row>
        </form>
      </div>

      <!-- Không có quyền xem request logs -->
      <div v-else-if="permChecked && !canViewRequestLogs" class="sdk-no-permission">
        <i class="fas fa-lock fa-3x mb-3" />
        <h5>Bạn chưa được cấp quyền xem log</h5>
        <p class="text-muted">
          Tính năng tra cứu request log và sự kiện dịch vụ yêu cầu quyền
          <code>Admin:RequestLogViewers</code>. Liên hệ quản trị viên hệ thống để được cấp quyền.
        </p>
        <b-button variant="light" @click="$router.push({ name: 'sdk_partner' })">
          <i class="fas fa-arrow-left mr-1" />Quay lại
        </b-button>
      </div>

      <template v-else>
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h4 class="mb-0">Tra cứu Request</h4>
          <b-button class="btn btn-light" @click="$router.push({ name: 'sdk_partner' })">
            <i class="fas fa-arrow-left mr-1" />Quay lại
          </b-button>
        </div>

        <!-- Tabs: Request Log | Sự kiện dịch vụ -->
        <b-tabs v-model="mainTab">
          <!-- ============================================================ -->
          <!-- TAB 1: Request Log -->
          <!-- ============================================================ -->
          <b-tab title="Tra cứu Request">
            <!-- Search + filters -->
            <b-card class="mb-3 mt-3">
              <b-row>
                <b-col :cols="12" :md="3">
                  <b-form-group label="Mã lỗi (Request ID)">
                    <b-form-input
                      v-model="rl.filter.requestId"
                      placeholder="Nhập requestId..."
                      @keyup.enter="rlApplyFilter"
                    />
                  </b-form-group>
                </b-col>
                <b-col :cols="12" :md="3">
                  <b-form-group label="Số điện thoại">
                    <b-form-input
                      v-model="rl.filter.phone"
                      placeholder="0909..."
                      @keyup.enter="rlApplyFilter"
                    />
                  </b-form-group>
                </b-col>
                <b-col :cols="12" :md="3">
                  <b-form-group label="Partner ID">
                    <b-form-input
                      v-model="rl.filter.partnerId"
                      placeholder="Để trống = tất cả"
                      @keyup.enter="rlApplyFilter"
                    />
                  </b-form-group>
                </b-col>
                <b-col :cols="12" :md="3">
                  <b-form-group label="HTTP Status">
                    <b-form-select v-model="rl.filter.status" :options="statusOptions" @change="rlApplyFilter" />
                  </b-form-group>
                </b-col>
                <b-col :cols="12" :md="3">
                  <b-form-group label="Service Type">
                    <b-form-select v-model="rl.filter.serviceType" :options="serviceTypeOptions" @change="rlApplyFilter" />
                  </b-form-group>
                </b-col>
                <b-col :cols="12" :md="4">
                  <b-form-group label="Khoảng thời gian">
                    <date-picker
                      v-model="rl.filter.range"
                      :range="true"
                      input-class="form-control"
                      type="date"
                      format="DD/MM/YYYY"
                      value-type="date"
                      style="width: 100%"
                      @change="rlApplyFilter"
                    />
                  </b-form-group>
                </b-col>
                <b-col :cols="12" :md="2" class="d-flex align-items-end">
                  <b-form-group label="&nbsp;" label-class="sdk-reset-label">
                    <b-button variant="outline-secondary" class="w-100" @click="rlResetFilter">
                      <i class="fas fa-redo mr-1" />Đặt lại
                    </b-button>
                  </b-form-group>
                </b-col>
                <b-col :cols="12" :md="3" class="d-flex align-items-end">
                  <b-form-group label="&nbsp;" label-class="sdk-reset-label">
                    <b-button variant="primary" class="w-100" @click="rlApplyFilter">
                      <i class="fas fa-search mr-1" />Tìm kiếm
                    </b-button>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card>

            <b-alert v-if="rl.error" show variant="danger" dismissible @dismissed="rl.error = null">
              {{ rl.error }}
            </b-alert>

            <!-- Table -->
            <template-table
              :column="rlColumns"
              :data="rl.items"
              :paging="rl.paging"
              :tableAction="false"
              :selectAction="false"
              :searchAction="false"
            >
              <template v-slot:body="{ item }">
                <td>{{ formatDateTime(item.createdAt) }}</td>
                <td class="sdk-mono-sm">{{ item.requestId }}</td>
                <td>{{ item.partnerName || item.partnerId }}</td>
                <td>{{ item.phone }}</td>
                <td><b-badge :variant="methodVariant(item.method)">{{ item.method }}</b-badge></td>
                <td class="sdk-mono-sm" :title="item.path">{{ truncatePath(item.path) }}</td>
                <td><b-badge :variant="item.serviceType ? 'light' : 'secondary'">{{ item.serviceType || '—' }}</b-badge></td>
                <td>
                  <b-badge :variant="statusVariant(item.statusCode)">{{ item.statusCode }}</b-badge>
                </td>
                <td>{{ item.durationMs }}</td>
                <td>
                  <b-button size="sm" variant="outline-primary" @click="openDetail(item)">
                    <i class="fas fa-eye" />
                  </b-button>
                </td>
              </template>
            </template-table>
          </b-tab>

          <!-- ============================================================ -->
          <!-- TAB 2: Sự kiện dịch vụ -->
          <!-- ============================================================ -->
          <b-tab title="Sự kiện dịch vụ">
            <b-card class="mb-3 mt-3">
              <b-row>
                <b-col :cols="12" :md="3">
                  <b-form-group label="Số điện thoại">
                    <b-form-input
                      v-model="ev.filter.phone"
                      placeholder="0909..."
                      @keyup.enter="evApplyFilter"
                    />
                  </b-form-group>
                </b-col>
                <b-col :cols="12" :md="3">
                  <b-form-group label="Partner ID">
                    <b-form-input v-model="ev.filter.partnerId" placeholder="Để trống = tất cả" @keyup.enter="evApplyFilter" />
                  </b-form-group>
                </b-col>
                <b-col :cols="12" :md="3">
                  <b-form-group label="Loại sự kiện">
                    <b-form-select v-model="ev.filter.eventType" :options="eventTypeOptions" @change="evApplyFilter" />
                  </b-form-group>
                </b-col>
                <b-col :cols="12" :md="3">
                  <b-form-group label="Dịch vụ">
                    <b-form-select v-model="ev.filter.service" :options="serviceTypeOptions" @change="evApplyFilter" />
                  </b-form-group>
                </b-col>
                <b-col :cols="12" :md="4">
                  <b-form-group label="Khoảng thời gian">
                    <date-picker
                      v-model="ev.filter.range"
                      :range="true"
                      input-class="form-control"
                      type="date"
                      format="DD/MM/YYYY"
                      value-type="date"
                      style="width: 100%"
                      @change="evApplyFilter"
                    />
                  </b-form-group>
                </b-col>
                <b-col :cols="12" :md="2" class="d-flex align-items-end">
                  <b-form-group label="&nbsp;" label-class="sdk-reset-label">
                    <b-button variant="outline-secondary" class="w-100" @click="evResetFilter">
                      <i class="fas fa-redo mr-1" />Đặt lại
                    </b-button>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card>

            <b-alert v-if="ev.error" show variant="danger" dismissible @dismissed="ev.error = null">
              {{ ev.error }}
            </b-alert>

            <template-table
              :column="evColumns"
              :data="ev.items"
              :paging="ev.paging"
              :tableAction="false"
              :selectAction="false"
              :searchAction="false"
            >
              <template v-slot:body="{ item }">
                <td>{{ formatDateTime(item.createdAt) }}</td>
                <td><b-badge variant="light">{{ item.eventType }}</b-badge></td>
                <td>{{ item.partnerName || item.partnerId }}</td>
                <td>{{ item.phone }}</td>
                <td>{{ item.service }}</td>
                <td>{{ item.resourceId }}</td>
                <td>
                  <b-badge :variant="item.result === 'ok' ? 'success' : item.result === 'blocked' ? 'warning' : 'danger'">
                    {{ item.result }}
                  </b-badge>
                </td>
                <td class="sdk-mono-sm">{{ item.requestId }}</td>
              </template>
            </template-table>
          </b-tab>
        </b-tabs>

        <!-- Detail Modal -->
        <b-modal
          v-model="detail.show"
          size="xl"
          :title="`Chi tiết request — ${detail.item && detail.item.requestId || ''}`"
          ok-only
          ok-title="Đóng"
          scrollable
        >
          <div v-if="detail.loading" class="text-center py-4">
            <b-spinner variant="primary" />
          </div>
          <div v-else-if="detail.error" class="alert alert-danger">{{ detail.error }}</div>
          <div v-else-if="detail.data">
            <b-row class="mb-3">
              <b-col :cols="6" :md="4"><strong>Request ID:</strong> <span class="sdk-mono-sm">{{ detail.data.requestId }}</span></b-col>
              <b-col :cols="6" :md="4"><strong>Thời gian:</strong> {{ formatDateTime(detail.data.createdAt) }}</b-col>
              <b-col :cols="6" :md="4"><strong>Partner:</strong> {{ detail.data.partnerName || detail.data.partnerId }}</b-col>
              <b-col :cols="6" :md="4"><strong>SĐT:</strong> {{ detail.data.phone }}</b-col>
              <b-col :cols="6" :md="4"><strong>User ID:</strong> {{ detail.data.userId }}</b-col>
              <b-col :cols="6" :md="4">
                <strong>Status:</strong>
                <b-badge :variant="statusVariant(detail.data.statusCode)" class="ml-1">{{ detail.data.statusCode }}</b-badge>
              </b-col>
              <b-col :cols="12" :md="8"><strong>Path:</strong> <span class="sdk-mono-sm">{{ detail.data.method }} {{ detail.data.path }}</span></b-col>
              <b-col :cols="6" :md="4"><strong>Duration:</strong> {{ detail.data.durationMs }} ms</b-col>
              <b-col :cols="6" :md="4"><strong>Service Type:</strong> {{ detail.data.serviceType }}</b-col>
            </b-row>

            <div v-if="detail.data.requestBody" class="mb-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <strong>Request Body</strong>
                <b-button size="sm" variant="outline-secondary" @click="copyToClipboard(detail.data.requestBody)">
                  <i class="fas fa-copy mr-1" />Copy
                </b-button>
              </div>
              <!-- Dùng <pre> để tránh XSS — không render HTML -->
              <pre class="sdk-body-pre">{{ prettyJson(detail.data.requestBody) }}</pre>
            </div>

            <div v-if="detail.data.responseBody" class="mb-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <strong>Response Body</strong>
                <b-button size="sm" variant="outline-secondary" @click="copyToClipboard(detail.data.responseBody)">
                  <i class="fas fa-copy mr-1" />Copy
                </b-button>
              </div>
              <pre class="sdk-body-pre">{{ prettyJson(detail.data.responseBody) }}</pre>
            </div>

            <div v-if="!detail.data.requestBody && !detail.data.responseBody" class="text-muted">
              Không có body được lưu cho request này.
            </div>
          </div>
        </b-modal>
      </template>
    </Wrapper>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import SdkPartnerService, { SERVICE_TYPES, EVENT_TYPES } from './services/sdk-partner.service';

export default {
  name: 'sdk_partner_request_logs',
  components: { 'date-picker': DatePicker },
  data() {
    return {
      isConfigured: SdkPartnerService.isConfigured(),
      needLogin: !SdkPartnerService.hasToken(),
      canViewRequestLogs: false,
      permChecked: false,
      loginLoading: false,
      loginForm: { email: '', password: '' },
      mainTab: 0,

      // Request Logs tab
      rl: {
        filter: { requestId: '', phone: '', partnerId: '', status: null, serviceType: null, range: null },
        items: [],
        paging: { page: 1, pageSize: 20, total: 0 },
        loading: false,
        error: null,
      },

      // Service Events tab
      ev: {
        filter: { phone: '', partnerId: '', eventType: null, service: null, range: null },
        items: [],
        paging: { page: 1, pageSize: 20, total: 0 },
        loading: false,
        error: null,
      },

      // Detail modal
      detail: { show: false, item: null, data: null, loading: false, error: null },

      statusOptions: [
        { value: null, text: 'Tất cả' },
        { value: '2xx', text: '2xx (Thành công)' },
        { value: '4xx', text: '4xx (Client Error)' },
        { value: '5xx', text: '5xx (Server Error)' },
        { value: '400', text: '400 Bad Request' },
        { value: '401', text: '401 Unauthorized' },
        { value: '403', text: '403 Forbidden' },
        { value: '404', text: '404 Not Found' },
        { value: '500', text: '500 Internal Error' },
      ],
      serviceTypeOptions: [
        { value: null, text: 'Tất cả' },
        ...SERVICE_TYPES.map((s) => ({ value: s, text: s })),
      ],
      eventTypeOptions: [
        { value: null, text: 'Tất cả' },
        ...EVENT_TYPES.map((e) => ({ value: e, text: e })),
      ],

      rlColumns: [
        { key: 'createdAt', label: 'Thời gian' },
        { key: 'requestId', label: 'Request ID' },
        { key: 'partner', label: 'Đối tác' },
        { key: 'phone', label: 'SĐT' },
        { key: 'method', label: 'Method' },
        { key: 'path', label: 'Path' },
        { key: 'serviceType', label: 'Service' },
        { key: 'statusCode', label: 'Status' },
        { key: 'durationMs', label: 'ms' },
        { key: 'actions', label: '' },
      ],
      evColumns: [
        { key: 'createdAt', label: 'Thời gian' },
        { key: 'eventType', label: 'Sự kiện' },
        { key: 'partner', label: 'Đối tác' },
        { key: 'phone', label: 'SĐT' },
        { key: 'service', label: 'Dịch vụ' },
        { key: 'resourceId', label: 'Resource ID' },
        { key: 'result', label: 'Kết quả' },
        { key: 'requestId', label: 'Request ID' },
      ],
    };
  },
  watch: {
    'rl.paging.page'() { this.loadRl(); },
    'rl.paging.pageSize'() { this.rl.paging.page = 1; this.loadRl(); },
    'ev.paging.page'() { this.loadEv(); },
    'ev.paging.pageSize'() { this.ev.paging.page = 1; this.loadEv(); },
    mainTab(val) {
      if (val === 0 && !this.rl.items.length) this.loadRl();
      if (val === 1 && !this.ev.items.length) this.loadEv();
    },
  },
  created() {
    this.refreshPermission();
  },
  methods: {
    async refreshPermission() {
      if (this.isConfigured && !this.needLogin) {
        this.canViewRequestLogs = await SdkPartnerService.ensureCanViewRequestLogs();
        if (this.canViewRequestLogs) this.loadRl();
      }
      this.permChecked = true;
    },
    formatDateTime(date) {
      if (!date) return '—';
      const m = this.$moment(date);
      return m.isValid() ? m.format('DD/MM/YYYY HH:mm:ss') : '—';
    },
    truncatePath(path) {
      if (!path) return '—';
      return path.length > 60 ? path.slice(0, 57) + '...' : path;
    },
    methodVariant(method) {
      const map = { GET: 'info', POST: 'success', PUT: 'warning', DELETE: 'danger', PATCH: 'secondary' };
      return map[method] || 'light';
    },
    statusVariant(code) {
      if (!code) return 'secondary';
      const n = parseInt(code, 10);
      if (n >= 200 && n < 300) return 'success';
      if (n >= 400 && n < 500) return 'warning';
      if (n >= 500) return 'danger';
      return 'light';
    },
    prettyJson(value) {
      if (!value) return '';
      if (typeof value === 'string') {
        try { return JSON.stringify(JSON.parse(value), null, 2); } catch { return value; }
      }
      try { return JSON.stringify(value, null, 2); } catch { return String(value); }
    },
    copyToClipboard(value) {
      const text = this.prettyJson(value);
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
          this.$toastr.s({ msg: 'Đã copy!', preventDuplicates: true });
        });
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        this.$toastr.s({ msg: 'Đã copy!', preventDuplicates: true });
      }
    },

    // ---- Request Logs ----
    rlBuildParams() {
      const p = { page: this.rl.paging.page, pageSize: this.rl.paging.pageSize };
      if (this.rl.filter.requestId) p.requestId = this.rl.filter.requestId.trim();
      if (this.rl.filter.phone) p.phone = this.rl.filter.phone.trim();
      if (this.rl.filter.partnerId) p.partnerId = this.rl.filter.partnerId.trim();
      if (this.rl.filter.status) p.status = this.rl.filter.status;
      if (this.rl.filter.serviceType) p.serviceType = this.rl.filter.serviceType;
      if (this.rl.filter.range && this.rl.filter.range[0]) {
        p.from = this.$moment(this.rl.filter.range[0]).format('YYYY-MM-DD');
      }
      if (this.rl.filter.range && this.rl.filter.range[1]) {
        p.to = this.$moment(this.rl.filter.range[1]).format('YYYY-MM-DD');
      }
      return p;
    },
    async loadRl() {
      this.$store.commit('context/setLoading', true);
      this.rl.error = null;
      try {
        const res = await SdkPartnerService.getRequestLogs(this.rlBuildParams());
        const data = (res && res.data) ? res.data : res;
        this.rl.items = (data && data.items) || [];
        this.rl.paging.total = (data && data.total) || 0;
      } catch (err) {
        this.rl.error = this.mapError(err);
        if (err && err.message === 'SDK_ADMIN_NO_TOKEN') { this.needLogin = true; }
      } finally {
        this.$store.commit('context/setLoading', false);
      }
    },
    rlApplyFilter() { this.rl.paging.page = 1; this.loadRl(); },
    rlResetFilter() {
      this.rl.filter = { requestId: '', phone: '', partnerId: '', status: null, serviceType: null, range: null };
      this.rl.paging.page = 1;
      this.loadRl();
    },
    async openDetail(item) {
      this.detail.show = true;
      this.detail.item = item;
      this.detail.data = null;
      this.detail.error = null;
      this.detail.loading = true;
      try {
        const res = await SdkPartnerService.getRequestLogDetail(item.id);
        this.detail.data = (res && res.data) ? res.data : res;
      } catch (err) {
        this.detail.error = this.mapError(err);
      } finally {
        this.detail.loading = false;
      }
    },

    // ---- Service Events ----
    evBuildParams() {
      const p = { page: this.ev.paging.page, pageSize: this.ev.paging.pageSize };
      if (this.ev.filter.phone) p.phone = this.ev.filter.phone.trim();
      if (this.ev.filter.partnerId) p.partnerId = this.ev.filter.partnerId.trim();
      if (this.ev.filter.eventType) p.eventType = this.ev.filter.eventType;
      if (this.ev.filter.service) p.service = this.ev.filter.service;
      if (this.ev.filter.range && this.ev.filter.range[0]) {
        p.from = this.$moment(this.ev.filter.range[0]).format('YYYY-MM-DD');
      }
      if (this.ev.filter.range && this.ev.filter.range[1]) {
        p.to = this.$moment(this.ev.filter.range[1]).format('YYYY-MM-DD');
      }
      return p;
    },
    async loadEv() {
      this.$store.commit('context/setLoading', true);
      this.ev.error = null;
      try {
        const res = await SdkPartnerService.getServiceEvents(this.evBuildParams());
        const data = (res && res.data) ? res.data : res;
        this.ev.items = (data && data.items) || [];
        this.ev.paging.total = (data && data.total) || 0;
      } catch (err) {
        this.ev.error = this.mapError(err);
        if (err && err.message === 'SDK_ADMIN_NO_TOKEN') { this.needLogin = true; }
      } finally {
        this.$store.commit('context/setLoading', false);
      }
    },
    evApplyFilter() { this.ev.paging.page = 1; this.loadEv(); },
    evResetFilter() {
      this.ev.filter = { phone: '', partnerId: '', eventType: null, service: null, range: null };
      this.ev.paging.page = 1;
      this.loadEv();
    },

    // ---- Auth ----
    async doLogin() {
      this.loginLoading = true;
      try {
        await SdkPartnerService.login(this.loginForm.email, this.loginForm.password);
        this.needLogin = false;
        await this.refreshPermission();
      } catch (err) {
        this.$toastr.e({
          title: 'Lỗi đăng nhập',
          msg: (err && err.message) || 'Đăng nhập thất bại',
          preventDuplicates: true,
        });
      } finally {
        this.loginLoading = false;
      }
    },

    mapError(err) {
      if (!err) return 'Đã có lỗi xảy ra';
      if (err.message === 'SDK_ADMIN_NO_TOKEN') return 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.';
      if (err.code === 403) return 'Bạn chưa được cấp quyền xem log (RequestLogAccessDenied).';
      return err.message || `Lỗi ${err.code || 'unknown'}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.sdk-login-panel {
  max-width: 780px;
  padding: 32px;
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 12px;
}

.sdk-no-permission {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
  color: #7e8299;
  i { color: #f64e60; }
  h5 { color: #3f4254; }
}

.sdk-mono-sm {
  font-family: 'Courier New', monospace;
  font-size: 12px;
}

.sdk-body-pre {
  background: #1e1e2d;
  color: #d1d5db;
  border-radius: 8px;
  padding: 16px;
  font-size: 12px;
  max-height: 400px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
  /* Đảm bảo không render HTML — chỉ text */
}
</style>
